import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";
import TableToolbar from "../../common_components/TableToolbar";
import CommonTable from "../../common_components/CommonTable";
import { SUMMARY_ROW_POSITIONS, EXPAND_TRIGGERS } from "../../common_components/tableConstants";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2024-02-01" -> "Feb 01, 2024" (parsed by hand so the timezone can't shift the day)
const formatDate = (iso) => {
    if (!iso)
        return "-";
    const [y, m, d] = iso.split("-");
    const mi = Number(m) - 1;
    return mi >= 0 && mi < 12 ? `${MONTHS[mi]} ${d}, ${y}` : iso;
};
const formatMoneyValue = (n) => (n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatCount = (n) => (n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
// Grey "$" with a small gap, same treatment as the listing page.
const Money = ({ value }) => (_jsxs(_Fragment, { children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), formatMoneyValue(value)] }));
// Client stays visible while the other columns scroll horizontally.
const PINNED_COLUMNS = { left: ["client"], right: [] };
const columns = [
    { id: "client", label: "Client", width: "200px", align: "left", sortable: true },
    { id: "credit", label: "# Of Accounts", width: "100px", align: "left", sortable: true },
    { id: "management_fees", label: "Mgmt. Fees", width: "150px", align: "left", sortable: true },
    { id: "total_commissions", label: "Total Commissions", width: "170px", align: "left", sortable: true },
    { id: "start_date", label: "Start Date", width: "140px", align: "left", sortable: true },
    { id: "termination_date", label: "Termination Date", width: "170px", align: "left", sortable: true },
    { id: "days", label: "# Of Days", width: "110px", align: "left", sortable: true },
    { id: "spend", label: "Spend", width: "140px", align: "left", sortable: true },
    { id: "revenue", label: "Revenue", width: "150px", align: "left", sortable: true },
    { id: "revenue_goal", label: "Revenue Goal", width: "160px", align: "left", sortable: true },
];
// "Present" (null) sorts after every real termination date.
const SORT_GETTERS = {
    client: (r) => (r.name || "").toLowerCase(),
    credit: (r) => r.credit || 0,
    management_fees: (r) => r.management_fees || 0,
    total_commissions: (r) => r.total_commissions || 0,
    start_date: (r) => r.start_date || "",
    termination_date: (r) => r.termination_date || "9999-99-99",
    days: (r) => r.days ?? 0,
    spend: (r) => r.spend || 0,
    revenue: (r) => r.revenue || 0,
    revenue_goal: (r) => r.revenue_goal || 0,
};
const summaryCellSx = { py: 1, px: 0.5, fontWeight: 700, color: "#181d27", whiteSpace: "nowrap" };
export default function EmployeeAccountsModal({ open, onClose, title, monthLabel, rows }) {
    const [search, setSearch] = useState("");
    const [isFull, setIsFull] = useState(false);
    const [sort, setSort] = useState(null);
    // Fresh state every time the modal opens.
    useEffect(() => {
        if (open) {
            setSearch("");
            setIsFull(false);
            setSort(null);
        }
    }, [open]);
    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = q ? rows.filter((r) => (r.name || "").toLowerCase().includes(q)) : rows;
        const getter = sort && SORT_GETTERS[sort.columnId];
        if (sort && getter) {
            const dir = sort.direction === "desc" ? -1 : 1;
            list = [...list].sort((a, b) => {
                const av = getter(a);
                const bv = getter(b);
                if (av < bv)
                    return -1 * dir;
                if (av > bv)
                    return 1 * dir;
                return 0;
            });
        }
        return list;
    }, [rows, search, sort]);
    const renderCell = (row, col) => {
        switch (col.id) {
            case "client": return row.name;
            case "credit": return formatCount(row.credit);
            case "management_fees": return _jsx(Money, { value: row.management_fees });
            case "total_commissions": return _jsx(Money, { value: row.total_commissions });
            case "start_date": return formatDate(row.start_date);
            case "termination_date": return row.termination_date ? formatDate(row.termination_date) : "Present";
            case "days": return row.days === null || row.days === undefined ? "N/A" : row.days;
            case "spend": return _jsx(Money, { value: row.spend });
            case "revenue": return _jsx(Money, { value: row.revenue });
            case "revenue_goal": return _jsx(Money, { value: row.revenue_goal });
            default: return null;
        }
    };
    const renderSummaryRow = (tableRows, col) => {
        const sum = (pick) => tableRows.reduce((s, r) => s + (pick(r) || 0), 0);
        switch (col.id) {
            case "client": return _jsx(Box, { sx: summaryCellSx, children: "Total Summary" });
            case "credit": return _jsx(Box, { sx: summaryCellSx, children: formatCount(sum((r) => r.credit)) });
            case "management_fees": return _jsx(Box, { sx: summaryCellSx, children: _jsx(Money, { value: sum((r) => r.management_fees) }) });
            case "total_commissions": return _jsx(Box, { sx: summaryCellSx, children: _jsx(Money, { value: sum((r) => r.total_commissions) }) });
            case "days": return _jsx(Box, { sx: summaryCellSx, children: sum((r) => r.days ?? 0) });
            case "spend": return _jsx(Box, { sx: summaryCellSx, children: _jsx(Money, { value: sum((r) => r.spend) }) });
            case "revenue": return _jsx(Box, { sx: summaryCellSx, children: _jsx(Money, { value: sum((r) => r.revenue) }) });
            case "revenue_goal": return _jsx(Box, { sx: summaryCellSx, children: _jsx(Money, { value: sum((r) => r.revenue_goal) }) });
            default: return null;
        }
    };
    const HEADER_HEIGHT = 76;
    return (_jsxs(Dialog, { open: open, onClose: onClose, fullScreen: isFull, maxWidth: false, sx: {
            "& .MuiDialog-paper": isFull
                ? { borderRadius: 0 }
                : { width: "92vw", maxWidth: 1600, maxHeight: "88vh", borderRadius: "12px", overflow: "hidden" },
        }, children: [_jsxs(Box, { sx: {
                    height: HEADER_HEIGHT, flexShrink: 0, px: 2, display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: 2, borderBottom: "1px solid #EAECF0", bgcolor: "#fff",
                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 2, minWidth: 0 }, children: [_jsx(IconButton, { onClick: onClose, "aria-label": "Close", sx: { color: "#535862" }, children: _jsx(X, { size: 22 }) }), _jsx(Box, { sx: { width: "1px", height: 32, bgcolor: "#EAECF0" } }), _jsx(Typography, { sx: { fontFamily: "Inter, sans-serif", fontSize: "16px", fontWeight: 600, color: "#181D27", whiteSpace: "nowrap" }, children: title }), monthLabel && (_jsx(Box, { sx: { bgcolor: "#F2F4F7", color: "#475467", borderRadius: "16px", px: 1.5, py: 0.25 }, children: _jsx(Typography, { sx: { fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 500, color: "inherit", lineHeight: 1.5 }, children: monthLabel }) }))] }), _jsx(TableToolbar, { title: "", showSearch: true, showFullScreen: true, searchValue: search, onSearchChange: setSearch, searchPlaceholder: "Search clients...", 
                        // Expand the modal itself - the default would hide the app sidebar.
                        onFullScreenClick: () => setIsFull((f) => !f) })] }), _jsx(CommonTable, { columns: columns, rows: visibleRows, renderCell: renderCell, isLoading: false, stickyHeader: true, maxHeight: isFull ? `calc(100vh - ${HEADER_HEIGHT}px)` : `calc(88vh - ${HEADER_HEIGHT}px)`, expandTrigger: EXPAND_TRIGGERS.NONE, sortConfig: sort, onSortChange: (columnId, direction) => setSort(columnId && direction ? { columnId, direction } : null), emptyMessage: "No clients found.", enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, pinnedColumns: PINNED_COLUMNS, enableColumnFilters: false, enableColumnVisibility: false, enableSelection: false, enableSummaryRow: visibleRows.length > 0, renderSummaryRow: renderSummaryRow, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM })] }));
}
